import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Trash2, Plus, Users, ClipboardList, Upload } from "lucide-react";
import { toast } from "sonner";
import TaskDialog from "@/components/tasks/TaskDialog";

const STATUSES = ["scheduled", "in_progress", "completed", "cancelled", "over_budget"];

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function AdminTasksPage() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [worksites, setWorksites] = useState([]);
  const [status, setStatus] = useState("");
  const [workerFilter, setWorkerFilter] = useState("");
  const [config, setConfig] = useState({ allow_worker_created: false, default_reminder_lead_minutes: 15 });
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [wizard, setWizard] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { limit: 200 };
      if (status) params.status = status;
      if (workerFilter) params.worker_id = workerFilter;
      const [r, u, w, c] = await Promise.all([
        api.get("/tenant/tasks", { params }),
        api.get("/tenant/users"),
        api.get("/tenant/worksites"),
        api.get("/tenant/tasks-config"),
      ]);
      setItems(r.data.items || []);
      setUsers(u.data.items.filter((x) => x.status === "active"));
      setWorksites(w.data.items || []);
      setConfig(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [status, workerFilter]);
  useEffect(() => { load(); }, [load]);

  const saveConfig = async (patch) => {
    try {
      const next = { ...config, ...patch };
      const { data } = await api.patch("/tenant/tasks-config", next);
      setConfig(data);
      toast.success("Config saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async (t) => {
    if (!confirm(`Cancel task "${t.title}"?`)) return;
    try {
      await api.delete(`/tenant/tasks/${t.id}`);
      toast.success("Cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rows = useMemo(() => items, [items]);

  return (
    <div className="space-y-6" data-testid="admin-tasks-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Admin</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Task scheduling</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Assign scheduled work to individuals or bulk-assign to a group. Workers see their
          tasks on <code className="rounded bg-muted px-1 text-xs">/app/tasks</code> and can
          start them in one tap.
        </p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Config</CardTitle></CardHeader>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2">
            <Switch
              checked={config.allow_worker_created}
              onCheckedChange={(v) => saveConfig({ allow_worker_created: v })}
              data-testid="tasks-allow-worker-toggle"
            />
            <Label className="text-sm">Allow workers to create their own tasks</Label>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Reminder lead-time (min)</Label>
            <Input
              type="number" min={0} max={1440}
              value={config.default_reminder_lead_minutes}
              onChange={(e) => setConfig({ ...config, default_reminder_lead_minutes: Number(e.target.value) })}
              onBlur={(e) => saveConfig({ default_reminder_lead_minutes: Number(e.target.value) })}
              className="w-24"
              data-testid="tasks-lead-input"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <Select value={status || "__all__"} onValueChange={(v) => setStatus(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="tasks-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={workerFilter || "__all__"} onValueChange={(v) => setWorkerFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-64" data-testid="tasks-filter-worker"><SelectValue placeholder="Worker" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All workers</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setWizard(true)} data-testid="tasks-bulk-btn">
            <Users className="mr-1.5 h-4 w-4" /> Bulk assign
          </Button>
          <Button
            variant="ghost"
            disabled
            title="Coming in Phase 22"
            data-testid="tasks-csv-import-btn"
          >
            <Upload className="mr-1.5 h-4 w-4" /> Publish schedule (CSV) · soon
          </Button>
          <Button onClick={() => { setEditing(null); setDialog(true); }} data-testid="tasks-single-create-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Create task
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Worker</TableHead>
                <TableHead>Start</TableHead>
                <TableHead>Budget</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No tasks match those filters.</TableCell></TableRow>
              )}
              {rows.map((t) => {
                const u = users.find((x) => x.id === t.worker_id);
                return (
                  <TableRow key={t.id} data-testid={`admin-task-row-${t.id}`}>
                    <TableCell>
                      <button className="text-left text-primary hover:underline" onClick={() => { setEditing(t); setDialog(true); }} data-testid={`admin-task-edit-${t.id}`}>
                        {t.title}
                      </button>
                      {t.recurrence_rrule && <Badge variant="outline" className="ml-2 text-[10px]">series</Badge>}
                    </TableCell>
                    <TableCell className="text-sm">{u?.full_name || u?.email || t.worker_id}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{fmt(t.scheduled_start)}</TableCell>
                    <TableCell className="text-sm">{t.budget_minutes}m</TableCell>
                    <TableCell><Badge variant="secondary" className="capitalize">{t.status.replace("_", " ")}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={() => doDelete(t)} data-testid={`admin-task-delete-${t.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <TaskDialog
        open={dialog}
        onOpenChange={setDialog}
        initial={editing}
        workerOptions={users}
        worksiteOptions={worksites}
        onSaved={load}
      />

      <BulkAssignWizard
        open={wizard}
        onOpenChange={setWizard}
        users={users}
        worksites={worksites}
        onSaved={load}
      />
    </div>
  );
}


function BulkAssignWizard({ open, onOpenChange, users, worksites, onSaved }) {
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [templateOpen, setTemplateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setSelected(new Set());
      setTemplate(null);
    }
  }, [open]);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const applyToAll = async (values) => {
    setSaving(true);
    const workerIds = Array.from(selected);
    let ok = 0;
    let bad = 0;
    for (const wid of workerIds) {
      try {
        await api.post("/tenant/tasks", { ...values, worker_id: wid });
        ok += 1;
      } catch (e) {
        bad += 1;
      }
    }
    setSaving(false);
    if (ok) toast.success(`Assigned to ${ok} worker${ok === 1 ? "" : "s"}`);
    if (bad) toast.error(`${bad} assignment${bad === 1 ? "" : "s"} failed`);
    onSaved?.();
    onOpenChange(false);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="bulk-assign-wizard">
          <SheetHeader className="text-left">
            <SheetTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-secondary" /> Bulk assign
            </SheetTitle>
            <SheetDescription className="text-xs uppercase tracking-widest text-muted-foreground">
              Step {step} of 2
            </SheetDescription>
          </SheetHeader>

          {step === 1 && (
            <div className="mt-4 space-y-2" data-testid="bulk-step-1">
              <p className="text-sm text-muted-foreground">Pick the workers to receive this task.</p>
              <div className="max-h-96 overflow-y-auto rounded-md border border-border">
                {users.map((u) => (
                  <label
                    key={u.id}
                    className="flex cursor-pointer items-center gap-2 border-b border-border/40 px-3 py-2 text-sm last:border-b-0 hover:bg-accent"
                  >
                    <Checkbox
                      checked={selected.has(u.id)}
                      onCheckedChange={() => toggle(u.id)}
                      data-testid={`bulk-user-${u.id}`}
                    />
                    <span>
                      <span className="font-medium">{u.full_name || u.email}</span>{" "}
                      <span className="text-muted-foreground">· {u.email}</span>
                    </span>
                  </label>
                ))}
              </div>
              <div className="flex items-center justify-between pt-3">
                <Badge variant="secondary">{selected.size} selected</Badge>
                <Button
                  onClick={() => { setStep(2); setTemplateOpen(true); }}
                  disabled={selected.size === 0}
                  data-testid="bulk-next-btn"
                >
                  Next: task template →
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="mt-4 space-y-3" data-testid="bulk-step-2">
              <p className="text-sm text-muted-foreground">
                Open the task dialog to configure. Values are applied to every selected worker.
              </p>
              <Button onClick={() => setTemplateOpen(true)} data-testid="bulk-template-open-btn">
                <Plus className="mr-1.5 h-4 w-4" /> Configure template
              </Button>
              {template && (
                <div className="rounded-lg border border-border bg-accent/40 p-3 text-xs">
                  <div className="font-medium">{template.title}</div>
                  <div className="text-muted-foreground">
                    {fmt(template.scheduled_start)} · {template.budget_minutes}m budget
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
                <Button
                  onClick={() => template && applyToAll(template)}
                  disabled={!template || saving}
                  data-testid="bulk-apply-btn"
                >
                  {saving ? "Assigning…" : `Assign to ${selected.size}`}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <TaskDialog
        open={templateOpen}
        onOpenChange={setTemplateOpen}
        worksiteOptions={worksites}
        workerOptions={[]}
        initial={template}
        onSubmitOverride={async (body) => {
          setTemplate({ ...body });
        }}
      />
    </>
  );
}
